import { Injectable } from '@angular/core';
import { DefaultTranslations, REFRESH_VERSION_INTERVAL } from '@app/app.constants';
import { PortalRepository } from '@app/core/repositories/portal-repository';
import { AppInfo, ConfigKey } from '@app/portal/models/app-info';
import { Branding } from '@app/portal/models/branding';
import { PortalVersion } from '@app/portal/models/portal-version';
import { TranslationMeta } from '@app/translate/models/translation-meta';
import { cached, Cached, RetentionPolicy } from '@app/utils/cached';
import { loggerFor } from '@app/utils/logger';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class PortalDataService {
  private _appInfo: Cached<AppInfo>;

  get appInfo() {
    this._appInfo ??= cached({
      dataProducer: () => this.portalRepository.appInfo(),
      id: 'portal:application-info',
      retention: RetentionPolicy.Forever,
    });

    return this._appInfo;
  }

  private _branding: Cached<Branding>;

  get branding() {
    this._branding ??= cached({
      dataProducer: () => this.portalRepository.branding(),
      id: 'portal:branding',
      retention: RetentionPolicy.Forever,
    });

    return this._branding;
  }

  private _translationMeta: Cached<TranslationMeta>;

  get translationMeta(): Cached<TranslationMeta> {
    this._translationMeta ??= cached({
      dataProducer: () =>
        this.portalRepository.translationMeta().pipe(
          catchError(() => {
            this.log.warn('Translation metadata does not exist for current branding');
            return of(DefaultTranslations);
          })
        ),
      id: 'portal:translation-meta',
      retention: RetentionPolicy.Forever,
    });

    return this._translationMeta;
  }

  private _version: Cached<PortalVersion>;

  get version() {
    this._version ??= cached({
      dataProducer: () => this.portalRepository.version(),
      id: 'portal:version',
      reloadInterval: REFRESH_VERSION_INTERVAL,
      retention: RetentionPolicy.Forever,
    });

    return this._version;
  }

  private log = loggerFor('PortalDataService');

  constructor(private portalRepository: PortalRepository) {}

  getConfigKey$(configKey: ConfigKey): Observable<string> {
    return this.appInfo.data$.pipe(map(({ config }) => config[configKey]));
  }

  updateAppInfo(enterpriseId: string): void {
    if (enterpriseId != null) {
      this.appInfo.changeIdentity(`portal:application-info:enterprise:${enterpriseId}`, () =>
        this.portalRepository.appInfo(enterpriseId)
      );
    } else {
      this.appInfo.changeIdentity(`portal:application-info`, () => this.portalRepository.appInfo());
    }
  }
}
